"use client";

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";

import { persistServerLocale } from "@/src/shared/i18n/localeCookie";
import { loadLocaleMessages } from "@/src/shared/i18n/localeMessages";
import { ShellI18nContext, type LocaleMessages } from "@smartmanager/shell-ui/shared/i18n/i18nContext";
import {
  DEFAULT_REQUEST_LOCALE,
  supportedLocale,
  type SupportedLocale,
} from "@smartmanager/shell-ui/shared/i18n/requestLocale";
import { isElectron } from "@/src/shared/platform";

/**
 * Serves one locale's translations to the client tree. The server resolves the
 * locale and loads its messages, so the first paint is already translated.
 * Key it by locale: a server-side locale change then remounts it with fresh state.
 */
export function I18nProvider({
  locale,
  messages,
  children,
}: {
  locale: SupportedLocale;
  messages: LocaleMessages;
  children: ReactNode;
}) {
  const router = useRouter();
  const [active, setActive] = useState({ locale, messages });
  const requestedLocale = useRef<SupportedLocale>(locale);
  // Web only: the switch asked for, shown once the server re-render remounts this.
  const [pendingWebLocale, setPendingWebLocale] = useState<SupportedLocale>(locale);

  const setLocale = useCallback((value: string) => {
    const next = supportedLocale(value) ?? DEFAULT_REQUEST_LOCALE;
    persistServerLocale(next);
    if (next === requestedLocale.current) return;
    requestedLocale.current = next;

    if (!isElectron) {
      setPendingWebLocale(next);
      // The server re-renders the layout for the new cookie and remounts this
      // provider with that locale's messages, so text never flashes untranslated.
      router.refresh();
      return;
    }

    // The desktop shell is a static export with no server to re-render, so
    // load the catalog here and swap locale and messages in one update.
    loadLocaleMessages(next).then(
      (nextMessages) => {
        if (requestedLocale.current === next) setActive({ locale: next, messages: nextMessages });
      },
      (error: unknown) => {
        console.error(`Failed to load ${next} translations:`, error);
        if (requestedLocale.current === next) requestedLocale.current = active.locale;
      },
    );
  }, [active.locale, router]);

  const value = useMemo(
    () => ({
      locale: active.locale,
      // Desktop reports only a switch whose catalog loaded: a failed load never shows, so it is never reported.
      requestedLocale: isElectron ? active.locale : pendingWebLocale,
      messages: active.messages,
      setLocale,
    }),
    [active, pendingWebLocale, setLocale],
  );
  return <ShellI18nContext.Provider value={value}>{children}</ShellI18nContext.Provider>;
}
